import { createAuthClient } from "better-auth/react";
import { LogIn, LogOut, UserRound } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { trackEvent } from "./analytics";

export const authClient = createAuthClient();

/** Header/inline trigger: links to the /login page. */
export function AuthPanel({
  language,
  compact = false,
}: {
  language: "zh" | "en";
  compact?: boolean;
}) {
  const session = authClient.useSession();
  const zh = language === "zh";

  if (session.data?.user) {
    return (
      <div className="auth-user">
        <a className="auth-user-link" href="/account">
          <UserRound size={16} />
          {session.data.user.name}
        </a>
        <button type="button" className="text-button" onClick={() => authClient.signOut()}>
          <LogOut size={15} />
          {zh ? "退出" : "Sign out"}
        </button>
      </div>
    );
  }

  return (
    <a className={compact ? "text-button" : "primary-button"} href="/login">
      <LogIn size={17} />
      {zh ? "登录 / 注册" : "Sign in"}
    </a>
  );
}

type Mode = "signin" | "signup" | "reset";

/** Full-page sign-in / sign-up / password-reset, rendered by the /login route. */
export function AuthPage({ language }: { language: "zh" | "en" }) {
  const zh = language === "zh";
  const session = authClient.useSession();
  const [mode, setMode] = useState<Mode>("signin");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [passwordResetEnabled, setPasswordResetEnabled] = useState(false);

  useEffect(() => {
    void fetch("/api/config")
      .then(
        async (response) =>
          (await response.json()) as {
            features?: { googleLogin?: boolean; passwordReset?: boolean };
          },
      )
      .then((value) => {
        setGoogleEnabled(Boolean(value.features?.googleLogin));
        setPasswordResetEnabled(Boolean(value.features?.passwordReset));
      })
      .catch(() => {
        setGoogleEnabled(false);
        setPasswordResetEnabled(false);
      });
  }, []);

  useEffect(() => {
    if (session.data?.user) window.location.replace("/account");
  }, [session.data?.user]);

  const title =
    mode === "signin"
      ? zh
        ? "欢迎回来。"
        : "Welcome back."
      : mode === "signup"
        ? zh
          ? "创建账户。"
          : "Create your account."
        : zh
          ? "重置密码。"
          : "Reset your password.";

  const submitText = busy
    ? zh
      ? "请稍候…"
      : "Please wait…"
    : mode === "signin"
      ? zh
        ? "登录"
        : "Sign in"
      : mode === "signup"
        ? zh
          ? "创建账户"
          : "Create account"
        : zh
          ? "发送重置邮件"
          : "Send reset email";

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = String(form.get("email") ?? "");
    const password = String(form.get("password") ?? "");
    const name = String(form.get("name") ?? email.split("@")[0] ?? "User");
    setBusy(true);
    setMessage("");
    if (mode === "reset") {
      const response = await authClient.requestPasswordReset({
        email,
        redirectTo: `${window.location.origin}/reset-password`,
      });
      setBusy(false);
      setMessage(
        response.error
          ? (response.error.message ?? (zh ? "邮件发送失败" : "Could not send reset email"))
          : zh
            ? "如果账户存在，重置邮件已经发送。"
            : "If the account exists, a reset email has been sent.",
      );
      return;
    }
    const response =
      mode === "signin"
        ? await authClient.signIn.email({ email, password })
        : await authClient.signUp.email({ email, password, name });
    setBusy(false);
    if (response.error) {
      setMessage(response.error.message ?? (zh ? "登录失败" : "Authentication failed"));
      return;
    }
    if (mode === "signup") trackEvent("sign_up", { method: "email" });
    window.location.href = (() => {
      const next = new URLSearchParams(window.location.search).get("next") ?? "/account";
      // 只接受站内路径，避免开放重定向
      return next.startsWith("/") && !next.startsWith("//") ? next : "/account";
    })();
  }

  return (
    <section className="auth-page">
      <div className="auth-stage" aria-hidden="true">
        <span className={zh ? "eyebrow" : "kicker"}>
          {zh ? "结一 · 账户" : "NAME FILE / ENTRY"}
        </span>
        <h2>
          {zh ? (
            <>
              名，是给世界的
              <br />
              <i>第一份见面礼</i>。
            </>
          ) : (
            <>
              Your Chinese name
              <br />
              <em>is waiting.</em>
            </>
          )}
        </h2>
        <p>
          {zh
            ? "单次 ¥9.90 · 十次 ¥89 · 失败自动返还次数"
            : "From $4.99 · Ten-pack available · Credit back on failure"}
        </p>
        <div className="auth-stage-stamp">
          {zh ? (
            <>
              结
              <br />一
            </>
          ) : (
            <>
              ADMITTED
              <br />
              准入
            </>
          )}
        </div>
      </div>
      <div className="auth-card">
        <div
          className="auth-tabs"
          role="tablist"
          aria-label={zh ? "登录或注册" : "Sign in or sign up"}
        >
          <button
            type="button"
            className={mode === "signin" ? "auth-tab active" : "auth-tab"}
            onClick={() => setMode("signin")}
          >
            {zh ? "登录" : "Sign in"}
          </button>
          <button
            type="button"
            className={mode === "signup" ? "auth-tab active" : "auth-tab"}
            onClick={() => setMode("signup")}
          >
            {zh ? "注册" : "Create account"}
          </button>
        </div>
        <h1 className="auth-title">{title}</h1>
        <form key={mode} className="auth-form" onSubmit={submit}>
          {mode === "signup" && (
            <label>
              {zh ? "称呼" : "Your name"}
              <input
                name="name"
                required
                minLength={2}
                placeholder={zh ? "怎么称呼你" : "How to call you"}
              />
            </label>
          )}
          <label>
            {zh ? "邮箱" : "Email"}
            <input name="email" type="email" required placeholder="you@example.com" />
          </label>
          {mode !== "reset" && (
            <label>
              {zh ? "密码" : "Password"}
              <input
                name="password"
                type="password"
                required
                minLength={10}
                placeholder={zh ? "至少 10 位" : "10+ characters"}
              />
            </label>
          )}
          {message && <p className="form-error">{message}</p>}
          <button className="primary-button" type="submit" disabled={busy}>
            {submitText}
          </button>
        </form>
        {googleEnabled && mode !== "reset" && (
          <button
            type="button"
            className="secondary-button auth-google"
            onClick={() =>
              authClient.signIn.social({ provider: "google", callbackURL: "/account" })
            }
          >
            <svg className="google-g" viewBox="0 0 18 18" aria-hidden="true">
              <path
                fill="#4285F4"
                d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
              />
              <path
                fill="#34A853"
                d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A8.99 8.99 0 0 0 9 18z"
              />
              <path
                fill="#FBBC05"
                d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.28-1.72V4.96H.96A8.99 8.99 0 0 0 0 9c0 1.45.35 2.83.96 4.04l3.01-2.32z"
              />
              <path
                fill="#EA4335"
                d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.42 0 9 0A8.99 8.99 0 0 0 .96 4.96l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58z"
              />
            </svg>
            {zh ? "使用 Google 登录" : "Continue with Google"}
          </button>
        )}
        {mode === "signin" && passwordResetEnabled ? (
          <button type="button" className="text-button" onClick={() => setMode("reset")}>
            {zh ? "忘记密码？" : "Forgot your password?"}
          </button>
        ) : (
          mode === "reset" && (
            <button type="button" className="text-button" onClick={() => setMode("signin")}>
              {zh ? "返回登录" : "Back to sign in"}
            </button>
          )
        )}
      </div>
    </section>
  );
}

export function ResetPasswordPanel({ language }: { language: "zh" | "en" }) {
  const zh = language === "zh";
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const newPassword = String(new FormData(event.currentTarget).get("password") ?? "");
    setBusy(true);
    const response = await authClient.resetPassword({ newPassword, token });
    setBusy(false);
    setMessage(
      response.error
        ? (response.error.message ?? (zh ? "重置失败或链接已过期" : "Reset failed or link expired"))
        : zh
          ? "密码已更新，可以返回登录。"
          : "Password updated. You can now sign in.",
    );
  }
  return (
    <section className="reset-panel">
      <h1>{zh ? "设置新密码" : "Choose a new password"}</h1>
      {!token ? (
        <p>{zh ? "重置链接缺少令牌。" : "The reset link is missing its token."}</p>
      ) : (
        <form onSubmit={submit}>
          <input
            name="password"
            type="password"
            minLength={10}
            required
            placeholder={zh ? "新密码（至少 10 位）" : "New password (10+ characters)"}
          />
          <button className="primary-button" type="submit" disabled={busy}>
            {busy ? (zh ? "请稍候…" : "Please wait…") : zh ? "更新密码" : "Update password"}
          </button>
        </form>
      )}
      {message && <p>{message}</p>}
      <a className="text-button" href="/login">
        {zh ? "返回登录" : "Back to sign in"}
      </a>
    </section>
  );
}
